import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { UserContext } from '../context/UserContext';
import { useOpportunityStore, useOpportunityStoreBase, type Opportunity } from '../stores/opportunityStore';
import { useStageStore } from '../stores/stageStore';
import { STAGE_STATS_CACHE_KEY, notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { bulkDeleteOpportunities, getSalesFunnelStats, getOpportunities } from '../services/api';
import { useListSearch } from '../hooks/useListSearch';
import { pendingRowClass } from '../utils/pendingItem';
import { canModify, canAdminDelete } from '../utils/permissions';
import { CustomSelect, ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, RowActions, RowAction, StatusDot, Amount, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { stageTone } from '../utils/statusTone';
import { formatTableDate, useRememberedFlag, useRememberedState, useUrlPage, useSyncUrlPage, useUrlSearch } from '@redpanda/utils';
import { Role } from '@redpanda/types';
import OpportunityDialog from '../components/OpportunityDialog';
import SalesFunnelChart from '../components/SalesFunnelChart';
import { rememberOpportunityView } from '../utils/opportunityView';
import { OpportunityViewTabs, OpportunityMoreMenu } from '../components/OpportunityViewControls';
import { TableSkeleton } from '@redpanda/ui';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { useCustomerLimitCache } from '../hooks/useCustomerLimitCache';
import { useBulkActions, isAdmin } from '@redpanda/utils';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { getCustomerStatus, getArchivedRowClassName } from '../utils/customerUtils';


const OpportunityListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const userRole = user?.role as Role;
  const currentUserId = user?.id;

  const {
    opportunities,
    pagination,
    error,
    showDeleteConfirmModal,
    isDeleting,
    isPageLoading,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchOpportunities,
  } = useOpportunityStore();

  const {
    stages,
    getCachedStageCount,
    getCachedStages,
    fetchStages,
  } = useStageStore();

  // 階段篩選記住上次的選擇（同客戶的標籤篩選）；階段被刪掉時下面改回全部
  const [selectedStage, setSelectedStage] = useRememberedState<string>('crm-ui:opportunity.stage', '');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<any>(null);
  const [showFunnelChart, setShowFunnelChart] = useRememberedFlag('crm-ui:opportunity.funnel', false);
  useEffect(() => rememberOpportunityView('list'), []); // 側欄「商機」下次開列表
  
  // 使用客戶限制快取 hook
  const {
    canAddCustomer,
    setCanAddCustomer,
    limitBannerRefreshTrigger,
  } = useCustomerLimitCache();
  
  // 快取 key 定義在 utils/dataEvents.ts（商機異動時由 notifyDataChanged 清除）
  const STATS_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24小時快取；商機異動、登出時清除
  
  const [stageStats, setStageStats] = useState<any[]>(() => {
    // 初始化時從快取載入
    try {
      const cached = localStorage.getItem(STAGE_STATS_CACHE_KEY);
      if (cached) {
        const cacheData = JSON.parse(cached);
        const isValid = Date.now() - cacheData.timestamp < STATS_CACHE_DURATION;
        if (isValid) {
          return cacheData.stats;
        }
      }
    } catch (error) {
      // 快取無效時返回空陣列
    }
    return [];
  });


  // 設定快取統計資料
  const setCachedStats = (stats: any[]) => {
    try {
      const cacheData = {
        stats,
        timestamp: Date.now()
      };
      localStorage.setItem(STAGE_STATS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache stage stats:', error);
    }
  };

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = { pathname: location.pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const initialPage = useUrlPage(urlPage); // 網址 ?page=、?q=：從詳情頁返回時回到同一頁、保留搜尋
  const [searchTerm, setSearchTerm] = useUrlSearch(urlPage);
  // 搜尋、階段篩選由後端查全部商機（原本只過濾目前這一頁）
  const search = searchTerm.trim();
  const hasFilters = !!search || !!selectedStage;
  const query = { search: search || undefined, stageId: selectedStage || undefined };
  const searchResult = useListSearch<Opportunity>('opportunities', hasFilters ? query : null, page => getOpportunities(page, query), initialPage);
  const filteredOpportunities = searchResult.active ? searchResult.items : opportunities;
  const displayPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  // 使用新的批次操作 hook
  const {
    selectedItems: selectedOpportunities,
    showBulkDeleteModal,
    isBulkDeleting,
    isItemSelected,
    allSelected,
    toggleItemSelection,
    toggleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    handlePageChange
  } = useBulkActions({
    items: filteredOpportunities,
    idField: 'id',
    entityName: t('opportunities.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteOpportunities,
    pagination: displayPagination ? {
      currentPage: displayPagination.currentPage,
      totalPages: displayPagination.totalPages
    } : undefined,
    fetchItems: fetchOpportunities,
    setCurrentPage,
    t,
    toast,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useOpportunityStoreBase.setState,
      'opportunity',
      () => opportunities,
      () => pagination
    ),
    // 商機只有管理員能刪（utils/permissions.ts，後端同規則）
    canDeleteItem: (opportunity: Opportunity) => canAdminDelete(opportunity, userRole),
    onSuccess: async (deletedIds) => {
      const handler = createBulkDeleteSuccessHandler(
        () => useOpportunityStoreBase.getState(),
        fetchOpportunities,
        setCurrentPage
      );
      await handler(deletedIds);
      // 漏斗、階段統計等重新載入（本頁的 loadStats 也由這個事件觸發）
      notifyDataChanged('opportunities');
    },
    userRole
  });

  // 載入商機列表和階段
  // 使用 ref 來穩定化函數引用，避免無限循環
  const fetchOpportunitiesRef = useRef(fetchOpportunities);
  const fetchStagesRef = useRef(fetchStages);
  fetchOpportunitiesRef.current = fetchOpportunities;
  fetchStagesRef.current = fetchStages;

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      // 頁面初始化時使用快取（如果有），避免每次切換都重新載入
      fetchOpportunitiesRef.current(initialPage, false);
      fetchStagesRef.current();
    }
  }, [isAuthenticated, currentUserId]);

  // 記住的階段已被刪除（階段管理）：改回全部
  useEffect(() => {
    if (selectedStage && stages.length > 0 && !stages.some(stage => stage.id === selectedStage)) setSelectedStage('');
  }, [stages, selectedStage, setSelectedStage]);

  // 載入統計資料
  const loadStats = React.useCallback(async () => {
    if (isAuthenticated && currentUserId) {
      try {
        const response = await getSalesFunnelStats();
        const newStats = response.data;
        
        // 更新統計資料和快取
        setStageStats(newStats);
        setCachedStats(newStats);
      } catch (error) {
        console.error('載入統計資料失敗:', error);
      }
    }
  }, [isAuthenticated, currentUserId]);

  // 初次載入統計資料；商機異動（API 成功後）再重新載入
  useEffect(() => {
    loadStats();
  }, [loadStats]);
  useDataChanged('opportunities', loadStats);



  // 處理編輯商機
  const handleEditOpportunity = (opportunity: any) => {
    setEditingOpportunity(opportunity);
    setShowEditDialog(true);
  };

  // 關閉對話框
  const handleCloseDialogs = () => {
    setShowAddDialog(false);
    setShowEditDialog(false);
    setEditingOpportunity(null);
  };

  // 使用從 API 獲取的全域統計資料
  const getStageStats = (stageId: string) => {
    const stat = stageStats.find(s => s.stage?.id === stageId);
    return stat ? { count: stat.count, totalValue: stat.totalValue } : undefined;
  };

  // 獲取階段名稱（包含快取支援）
  const getStageName = (stageId: string) => {
    // 先從已載入的 stages 中查找
    let stage = stages.find(s => s.id === stageId);
    
    // 如果找不到且 stages 為空，嘗試從快取中查找
    if (!stage && stages.length === 0) {
      const cachedStages = getCachedStages();
      const cachedStage = cachedStages.find(s => s.id === stageId);
      if (cachedStage) {
        return cachedStage.name;
      }
    }

    return stage?.name || t('opportunities.list.unknownStage', { ns: 'pages' });
  };

  // 獲取階段機率（包含快取支援）
  const getStageProbability = (stageId: string) => {
    // 先從已載入的 stages 中查找
    let stage = stages.find(s => s.id === stageId);

    // 如果找不到且 stages 為空，嘗試從快取中查找
    if (!stage && stages.length === 0) {
      const cachedStages = getCachedStages();
      const cachedStage = cachedStages.find(s => s.id === stageId);
      if (cachedStage) {
        return cachedStage.probability;
      }
    }

    return stage?.probability || 0;
  };

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : handlePageChange(page));
  useSyncUrlPage(urlPage, displayPagination, changePage);

  // 清除所有篩選
  const clearFilters = React.useCallback(() => {
    setSearchTerm('');
    setSelectedStage('');
  }, []);

  if (!isUserLoading && !isAuthenticated) {
    return (
      <div className="text-center text-red-600">
        {t('opportunities.list.pleaseLogin', { ns: 'pages' })}
      </div>
    );
  }

  const hasStages = stages.length > 0 || getCachedStages().length > 0;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('opportunities.list.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {/* 批量操作按鈕 */}
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={selectedOpportunities.length}
                isDeleting={isBulkDeleting}
                onDelete={handleBulkDelete}
              />
            )}

            {/* 新增商機（頁首唯一主要按鈕；階段已初始化才顯示） */}
            {hasStages && (
                <button
                  onClick={() => canAddCustomer && setShowAddDialog(true)}
                  disabled={!canAddCustomer}
                  className={canAddCustomer ? btnPrimary : btnDisabled}
                  title={canAddCustomer ? undefined : t('customers.list.limitReached', { ns: 'pages' })}
                >
                  {t('opportunities.list.addOpportunity', { ns: 'pages' })}
                </button>
            )}

            {/* 不常用的管理功能收進「⋯」（階段未初始化時也要能進階段管理）；放最後：items-baseline 取右側第一個元件的文字基線，只有圖示的「⋯」放第一個會把標題往下推 */}
            <OpportunityMoreMenu />
          </div>
        </div>
        {hasStages && (
          <OpportunityViewTabs current="list" showFunnel={showFunnelChart} onToggleFunnel={() => setShowFunnelChart(!showFunnelChart)}>
            {/* 提示文字較長（標題、描述、客戶姓名或公司），比其他列表的搜尋框寬一級 */}
            <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('opportunities.list.searchPlaceholder', { ns: 'pages' })} widthClass="md:w-96" />
            <div className="md:w-64">
              <CustomSelect
                value={selectedStage}
                onChange={(value) => setSelectedStage(value)}
                placeholder={t('opportunities.list.selectStagePlaceholder', { ns: 'pages' })}
                className="h-10"
                options={
                  stages.map(stage => ({
                    value: stage.id,
                    label: `${stage.name} (${Math.round(stage.probability * 100)}%)`
                  }))
                }
              />
            </div>
            {/* 清除篩選：只在有篩選條件時出現（§3-5） */}
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="self-start md:self-auto text-sm font-medium text-fg-secondary hover:text-fg transition-colors"
              >
                {t('opportunities.list.clearFilters', { ns: 'pages' })}
              </button>
            )}
          </OpportunityViewTabs>
        )}
      </div>

      {/* 客戶數量限制資訊 */}
      <CustomerLimitBanner 
        onLimitChange={setCanAddCustomer} 
        refreshTrigger={limitBannerRefreshTrigger}
      />

      {/* 銷售漏斗圖表 */}
      {showFunnelChart && (
        <div className="mb-8">
          <SalesFunnelChart />
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      {/* 階段摘要：6 張卡改成一個容器（§3-4）；點一下用該階段篩選 */}
      {(() => {
        const stagesToRender = stages.length > 0 ? stages : getCachedStages();
        if (stagesToRender.length === 0) {
          // 階段還沒載入：先放同高度的佔位格，避免資料回來時整頁往下跳（CLAUDE.md 防止骨架跳躍）
          const count = getCachedStageCount();
          return (
            <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 bg-surface rounded-lg border border-line overflow-hidden divide-x divide-line" aria-hidden="true">
              {Array.from({ length: count }, (_, i) => (
                <div key={i} className="px-4 py-3">
                  <div className="h-[18px] w-16 rounded bg-slate-100 dark:bg-slate-800" />
                  <div className="mt-1 h-7 w-8 rounded bg-slate-100 dark:bg-slate-800" />
                  <div className="mt-0.5 h-5 w-20 rounded bg-slate-100 dark:bg-slate-800" />
                </div>
              ))}
            </div>
          );
        }
        return (
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 bg-surface rounded-lg border border-line overflow-hidden divide-x divide-line">
            {stagesToRender.map((stage) => {
              const stats = getStageStats(stage.id);
              const active = selectedStage === stage.id;
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => setSelectedStage(active ? '' : stage.id)}
                  aria-pressed={active}
                  className={`px-4 py-3 text-left transition-colors ${active ? 'bg-accent-soft' : 'hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`text-[13px] truncate ${active ? 'text-accent font-medium' : 'text-fg-secondary'}`}>{stage.name}</span>
                    <span className="text-[13px] text-fg-muted tabular-nums">{Math.round(stage.probability * 100)}%</span>
                  </div>
                  <div className="mt-1 text-lg font-semibold text-fg tabular-nums">{stats?.count ?? 0}</div>
                  <div className="text-[13px] text-fg-secondary tabular-nums"><Amount value={stats?.totalValue ?? 0} /></div>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {/* 商機列表 */}
      {listLoading ? (
        <TableSkeleton rows={10} columns={7} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
            <TableHeader>
              <TableRow>
                {/* 批次選擇欄位 */}
                <BulkSelectHeader
                  userRole={userRole}
                  allSelected={allSelected}
                  onToggleAll={toggleSelectAll}
                  hasItems={filteredOpportunities.length > 0}
                />
                <TableHead className="w-52">{t('common.table.columns.title')}</TableHead>
                <TableHead className="w-40">{t('common.forms.customer')}</TableHead>
                <TableHead className="w-28">{t('opportunities.list.owner', { ns: 'pages' })}</TableHead>
                <TableHead className="w-28">{t('opportunities.list.stage', { ns: 'pages' })}</TableHead>
                <TableHead numeric className="w-32">{t('opportunities.list.value', { ns: 'pages' })}</TableHead>
                <TableHead className="w-28 pl-10">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOpportunities.length > 0 ? (
                filteredOpportunities.map((opportunity) => (
                  <TableRow key={opportunity.id} className={`${getArchivedRowClassName(opportunity.customer || {})} ${pendingRowClass(opportunity)}`}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={isItemSelected(opportunity.id)}
                      onToggle={() => toggleItemSelection(opportunity.id)}
                      canDelete={canAdminDelete(opportunity, userRole)}
                    />
                    <TableCell>
                      {/* 點標題進入詳情（取代「查看」操作） */}
                      <Link to={`/opportunities/${opportunity.id}`} className="block text-sm font-medium truncate hover:text-accent transition-colors">
                        {opportunity.title}
                      </Link>
                      {opportunity.description && (
                        <p className="text-[13px] text-fg-secondary truncate" title={opportunity.description}>
                          {opportunity.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm truncate">
                        {opportunity.customer ? (
                          <>
                            {opportunity.customer.name}
                            {getCustomerStatus(opportunity.customer).isArchived && (
                              <span className="ml-1 text-[13px] text-fg-secondary">({t('common.archived')})</span>
                            )}
                          </>
                        ) : (
                          <span className="text-red-600 dark:text-red-400 text-[13px]">{t('opportunities.list.customerMissing', { ns: 'pages' })}</span>
                        )}
                      </p>
                      {opportunity.customer?.company && (
                        <p className="text-[13px] text-fg-secondary truncate">
                          {opportunity.customer.company}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="truncate">
                      {opportunity.createdByName}
                    </TableCell>
                    <TableCell>
                      <StatusDot
                        tone={stageTone(getStageProbability(opportunity.currentStageId))}
                        label={getStageName(opportunity.currentStageId)}
                      />
                    </TableCell>
                    <TableCell numeric>
                      <Amount value={opportunity.value} />
                      <div className="text-[13px] text-fg-secondary">
                        {formatTableDate(opportunity.createdAt, i18n.language)}
                      </div>
                    </TableCell>
                    <TableCell className="pl-10">
                      <RowActions>
                        {canModify(opportunity, userRole, currentUserId) && (
                          <RowAction onClick={() => handleEditOpportunity(opportunity)}>{t('common.actions.edit')}</RowAction>
                        )}
                        {canAdminDelete(opportunity, userRole) && (
                          <RowAction tone="danger" onClick={() => handleDelete(opportunity.id)}>{t('common.actions.delete')}</RowAction>
                        )}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <ListEmptyRow
                  colSpan={7}
                  filtered={hasFilters}
                  title={t('opportunities.list.noOpportunitiesData', { ns: 'pages' })}
                  hint={t('opportunities.list.emptyHint', { ns: 'pages' })}
                  action={stages.length === 0 ? (
                    <Link to="/opportunities/stages" className="text-sm text-accent hover:underline">
                      {t('opportunities.list.goToStageManagement', { ns: 'pages' })}
                    </Link>
                  ) : canAddCustomer && (
                    <button type="button" onClick={() => setShowAddDialog(true)} className={btnPrimary}>{t('opportunities.list.addOpportunity', { ns: 'pages' })}</button>
                  )}
                />
              )}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      {/* 表格下方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} isLoading={listLoading} className="mt-4" />

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('opportunities.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('opportunities.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      {/* 新增商機對話框 */}
      <OpportunityDialog
        isOpen={showAddDialog}
        onClose={handleCloseDialogs}
      />

      {/* 編輯商機對話框 */}
      <OpportunityDialog
        isOpen={showEditDialog}
        onClose={handleCloseDialogs}
        opportunity={editingOpportunity}
      />

      {/* 批次刪除確認對話框 */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: selectedOpportunities.length, entity: t('opportunities.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: selectedOpportunities.length, entity: t('opportunities.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default OpportunityListPage;